import { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  createProgramVersion,
  deleteProgramVersion,
  renameProgramVersion,
} from 'common/src/api/commercialProgram';
import type {
  CommercialProgram,
  CommercialProgramVersion,
  ExporterReceiver,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { ConfirmModal } from '../../components/ui';
import MultiSelectWithActions from '../../components/common/MultiSelectWithActions';
import type { CommercialProgramState } from './useCommercialProgram';

export const CURRENT_VERSION = 'current';

interface ProgramToolbarProps {
  state: CommercialProgramState;
  program: CommercialProgram;
  canEdit: boolean;
  /** Guardar versión va solo en Gestionar compromisos. */
  canSaveVersion: boolean;
  versionId: string;
  onVersionChange: (versionId: string) => void;
  /** Clientes que se pueden filtrar: en Gestionar compromisos, los del programa. */
  receivers: ExporterReceiver[];
  selectedReceiverIds: Set<string>;
  onReceiversChange: (ids: Set<string>) => void;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

/**
 * Lo que comparten Resumen y Gestionar compromisos: qué programa se ve
 * (vigente o una versión, que se puede renombrar o eliminar) y filtrar
 * clientes. Guardar versión solo en Gestionar compromisos.
 */
export default function ProgramToolbar({
  state,
  program,
  canEdit,
  canSaveVersion,
  versionId,
  onVersionChange,
  receivers,
  selectedReceiverIds,
  onReceiversChange,
}: ProgramToolbarProps) {
  const { t } = useTranslation('commitments');
  const { versions, rows, userProfile, reloadProgramData } = state;
  // Lo elegido en la otra pestaña puede no estar entre estos clientes.
  const selectedIds = Array.from(selectedReceiverIds).filter((id) =>
    receivers.some((r) => String(r.id) === id)
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Guardar una versión nueva o renombrar una existente usan el mismo modal.
  const [nameModal, setNameModal] = useState<{
    version: CommercialProgramVersion | null;
    name: string;
  } | null>(null);
  const [toDelete, setToDelete] = useState<CommercialProgramVersion | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const version = versions.find((v) => String(v.id) === versionId) || null;
  const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);
  // Las que guardó el usuario y las que se crearon solas al cambiar la
  // unidad o la fecha base; las más nuevas primero.
  const newestFirst = [...versions].sort(
    (a, b) => time(b.as_of) - time(a.as_of)
  );
  const savedVersions = newestFirst.filter((v) => !v.is_automatic);
  const automaticVersions = newestFirst.filter((v) => v.is_automatic);

  // Siguiente "Versión N" libre (al borrar una no se repiten números).
  const nextVersionNumber =
    Math.max(
      savedVersions.length,
      ...savedVersions.map((v) => Number(v.name.match(/(\d+)/)?.[1]) || 0)
    ) + 1;

  const showError = (e: unknown) => {
    const message = e instanceof Error ? e.message : String(e);
    toast.error(t('No se pudo guardar: {{error}}', { error: message }));
  };

  const handleSaveName = async () => {
    if (!nameModal || !nameModal.name.trim()) return;
    setBusy(true);
    try {
      if (nameModal.version) {
        await renameProgramVersion(nameModal.version.id, nameModal.name.trim());
        await reloadProgramData();
      } else {
        await createProgramVersion({
          program_id: program.id,
          name: nameModal.name.trim(),
          date_basis: program.date_basis,
          created_by: userProfile?.id ?? null,
        });
        await reloadProgramData();
        // Recién guardada es igual al vigente: se sigue en "Vigente".
        toast.success(t('Versión guardada'));
      }
      setNameModal(null);
    } catch (e: unknown) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      await deleteProgramVersion(toDelete.id);
      if (String(toDelete.id) === versionId) onVersionChange(CURRENT_VERSION);
      await reloadProgramData();
      setToDelete(null);
      toast.success(t('Versión eliminada'));
    } catch (e: unknown) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  // Cómo está el vigente respecto de la última versión guardada.
  const lastVersion = savedVersions[0] ?? null;
  const changedSinceLast =
    !!lastVersion &&
    rows.some(
      (row) =>
        time(row.created_at) > time(lastVersion.as_of) ||
        time(row.deleted_at) > time(lastVersion.as_of)
    );
  const currentSubtitle = lastVersion
    ? changedSinceLast
      ? t('Con cambios desde «{{name}}»', { name: lastVersion.name })
      : t('Igual a «{{name}}»', { name: lastVersion.name })
    : null;

  const choose = (id: string) => {
    onVersionChange(id);
    setPickerOpen(false);
  };

  const pickerRow = (
    id: string,
    name: string,
    date: string | null,
    item: CommercialProgramVersion | null,
    automatic = false
  ) => {
    const selected = id === versionId;
    return (
      <div
        key={id}
        role='option'
        aria-selected={selected}
        tabIndex={0}
        onClick={() => choose(id)}
        onKeyDown={(e) => e.key === 'Enter' && choose(id)}
        className={`group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-default-100 ${
          selected ? 'bg-default-100' : ''
        }`}
      >
        {automatic && (
          <Icon
            icon='tabler:history'
            width={15}
            className='shrink-0 text-default-400'
          />
        )}
        <div className='min-w-0 flex-1'>
          <p className={`text-sm ${automatic ? 'text-default-500' : ''}`}>
            {name}
          </p>
          {date && <p className='text-xs text-default-400'>{date}</p>}
        </div>
        {item && canEdit && (
          <div className='flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100'>
            <button
              type='button'
              aria-label={t('Editar nombre')}
              onClick={(e) => {
                e.stopPropagation();
                setPickerOpen(false);
                setNameModal({ version: item, name: item.name });
              }}
              className='rounded p-1 text-default-400 hover:bg-default-200 hover:text-foreground'
            >
              <Icon icon='tabler:pencil' width={15} />
            </button>
            <button
              type='button'
              aria-label={t('Borrar versión')}
              onClick={(e) => {
                e.stopPropagation();
                setPickerOpen(false);
                setToDelete(item);
              }}
              className='rounded p-1 text-default-400 hover:bg-default-200 hover:text-danger'
            >
              <Icon icon='tabler:trash' width={15} />
            </button>
          </div>
        )}
        {selected && (
          <Icon
            icon='tabler:check'
            width={16}
            className='shrink-0 text-default-500'
          />
        )}
      </div>
    );
  };

  return (
    <div className='mb-4 flex flex-wrap items-center gap-2'>
      <Popover
        placement='bottom-start'
        isOpen={pickerOpen}
        onOpenChange={setPickerOpen}
      >
        <PopoverTrigger>
          {/* Se ve como un Select chico de NextUI, pero cada fila tiene sus acciones. */}
          <button
            type='button'
            aria-label={t('Versión')}
            className='flex h-12 w-72 items-center gap-2 rounded-medium bg-default-100 px-3 text-left hover:bg-default-200'
          >
            <span className='flex min-w-0 flex-1 flex-col'>
              <span className='text-tiny text-default-600'>{t('Versión')}</span>
              <span className='truncate text-small'>
                {version ? version.name : t('Vigente')}
              </span>
            </span>
            <Icon
              icon='tabler:chevron-down'
              width={16}
              className='text-default-500'
            />
          </button>
        </PopoverTrigger>
        <PopoverContent className='w-96 p-1'>
          <div
            role='listbox'
            className='flex max-h-80 w-full flex-col overflow-y-auto'
          >
            {pickerRow(CURRENT_VERSION, t('Vigente'), currentSubtitle, null)}
            {savedVersions.length > 0 && (
              <p className='px-2 pb-1 pt-2 text-xs text-default-400'>
                {t('Guardadas')}
              </p>
            )}
            {savedVersions.map((v) =>
              pickerRow(String(v.id), v.name, formatDate(v.as_of), v)
            )}
            {automaticVersions.length > 0 && (
              <p className='px-2 pb-1 pt-3 text-xs text-default-400'>
                {t('Automáticas, al cambiar la unidad o la fecha base')}
              </p>
            )}
            {automaticVersions.map((v) =>
              pickerRow(String(v.id), v.name, formatDate(v.as_of), v, true)
            )}
          </div>
        </PopoverContent>
      </Popover>
      {canEdit && canSaveVersion && (
        <Button
          size='sm'
          variant='flat'
          startContent={<Icon icon='tabler:bookmark-plus' width={16} />}
          onPress={() =>
            setNameModal({
              version: null,
              name: `${t('Versión')} ${nextVersionNumber}`,
            })
          }
        >
          {t('Guardar versión')}
        </Button>
      )}
      {receivers.length > 1 && (
        <MultiSelectWithActions
          size='sm'
          className='w-60'
          label={t('Clientes')}
          placeholder={t('Todos los clientes')}
          options={receivers.map((r) => ({
            value: String(r.id),
            label: r.name,
          }))}
          selectedKeys={selectedIds}
          onSelectionChange={(keys) => onReceiversChange(new Set(keys))}
          renderValue={() => {
            const ids = selectedIds;
            if (ids.length === 0 || ids.length === receivers.length)
              return t('Todos los clientes');
            return ids.length === 1
              ? (receivers.find((r) => String(r.id) === ids[0])?.name ?? '')
              : `${ids.length} ${t('clientes')}`;
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title={t('¿Eliminar la versión?')}
        confirmLabel={t('Eliminar')}
        isDestructive
        isLoading={busy}
      >
        <p className='text-sm text-default-500'>
          {t(
            'Se elimina "{{name}}" y ya no vas a poder comparar contra ella. Los compromisos del programa no cambian.',
            { name: toDelete?.name ?? '' }
          )}
        </p>
      </ConfirmModal>

      <Modal isOpen={!!nameModal} onClose={() => setNameModal(null)}>
        <ModalContent>
          <ModalHeader>
            {nameModal?.version ? t('Editar nombre') : t('Guardar versión')}
          </ModalHeader>
          <ModalBody>
            {!nameModal?.version && (
              <p className='text-sm text-default-500'>
                {t(
                  'Guarda el programa tal como está ahora, para comparar el cumplimiento contra lo que le comunicaste al cliente.'
                )}
              </p>
            )}
            <Input
              autoFocus
              label={t('Nombre')}
              placeholder={t('Ej. V1 – la que mandé en Hong Kong')}
              value={nameModal?.name ?? ''}
              onValueChange={(name) =>
                setNameModal((prev) => (prev ? { ...prev, name } : prev))
              }
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setNameModal(null)}>
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              onPress={handleSaveName}
              isLoading={busy}
              isDisabled={!nameModal?.name.trim()}
            >
              {t('Guardar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
